import { useState } from 'react';

import { Select, TextInput, toast } from '@pella/sash-framework';
import {
  useCarriers,
  useSelectionLists,
  usePlants,
  useUpdateRequest,
  useCreateTrailerToDockTransaction,
} from '../../../../hooks';
import { useAppContext } from '../../../../providers';
import { STATUSES } from '../../../../data/constants';
import { TransactionDrawer } from '../../../atoms/TransactionDrawer/TransactionDrawer';

import * as classes from './TrailerToDock.module.css';

/**
 * Drawer for the Trailer To Dock transaction.
 * Opens when a user clicks Complete on an Accepted request with transactionId === 'TRAILER_TO_DOCK'.
 * @param {Local.TrailerToDockDrawerProps} props - Component props
 */
export const TrailerToDockDrawer = ({ open, close, request }) => {
  const [dock, setDock] = useState('');
  const [carrierName, setCarrierName] = useState('');
  const [trailerNumber, setTrailerNumber] = useState('');

  const { selectedPlant } = useAppContext();
  const { data: plants } = usePlants(true);
  const { data: selectionLists } = useSelectionLists();
  const { data: carriers } = useCarriers();

  const { mutate: updateRequest } = useUpdateRequest();
  const { mutate: createTransaction, isPending } = useCreateTrailerToDockTransaction();

  const dockList = /** @type {Local.SelectionListWithValues | undefined} */ (
    selectionLists?.find((list) => list.id === request.action?.dockSelectionListId)
  );
  const dockValues = dockList?.values ?? [];

  const plant = plants?.find((p) => p.id === selectedPlant);
  const organizationCode = plant?.wmsOrgCode ?? '';

  const hasRequiredFields = Boolean(dock && carrierName && trailerNumber);
  const disableSubmit = !hasRequiredFields || isPending;

  /** Resets form state and closes the drawer */
  const handleClose = () => {
    setDock('');
    setCarrierName('');
    setTrailerNumber('');
    close();
  };

  /**
   * Updates the request to Completed status and closes the drawer.
   * @param {(request: Pella.ProductionRequestAPI.Request, onSettled: () => void) => void} completeWithLinkedActionCheck
   * @param {string} errorMessage - Toast message shown if the status update fails.
   */
  const handleCompleteRequest = (completeWithLinkedActionCheck, errorMessage) => {
    completeWithLinkedActionCheck(request, () =>
      updateRequest(
        { id: request.id, status: STATUSES.COMPLETED },
        {
          onSuccess: () => {
            toast.success('Request completed successfully!');
            handleClose();
          },
          onError: () => {
            toast.error(errorMessage);
          },
        },
      ),
    );
  };

  /**
   * Posts the transaction to WMS then marks the request complete
   * @param {(request: Pella.ProductionRequestAPI.Request, onSettled: () => void) => void} completeWithLinkedActionCheck
   */
  const handleSubmit = (completeWithLinkedActionCheck) => {
    if (!organizationCode) {
      toast.error('Cannot submit: the selected plant does not have a WMS Org Code configured.');
      return;
    }

    createTransaction(
      {
        dock,
        organizationCode,
        trailerNumber,
        carrierName,
      },
      {
        onSuccess: () =>
          handleCompleteRequest(
            completeWithLinkedActionCheck,
            'Request completed in WMS but failed to update status. Please refresh.',
          ),
        onError: () => {
          toast.error('Error submitting Trailer To Dock transaction. Please try again.');
        },
      },
    );
  };

  /**
   * Handles changes to the trailer number input field.
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event from the trailer number input field
   */
  const handleTrailerNumberChange = (e) => {
    setTrailerNumber(e.target.value);
  };

  return (
    <TransactionDrawer
      open={open}
      close={handleClose}
      request={request}
      disableSubmit={disableSubmit}
      onComplete={handleSubmit}
    >
      <div className={classes.fieldGrid}>
        <Select
          className={classes.selectItem}
          labelText='Dock'
          id='DockSelect'
          required
          value={dock}
          onChange={(e) => setDock(e.target.value)}
          data-testid='dock-select'
        >
          <option value=''>--- Select a Dock ---</option>
          {dockValues.map((value) => (
            <option key={value.id} value={value.description}>
              {value.description}
            </option>
          ))}
        </Select>

        <Select
          className={classes.selectItem}
          labelText='Carrier'
          id='CarrierSelect'
          required
          value={carrierName}
          onChange={(e) => setCarrierName(e.target.value)}
          data-testid='carrier-select'
        >
          <option value=''>--- Select a Carrier ---</option>
          {carriers?.map((carrier) => (
            <option key={carrier.carrierId} value={carrier.carrierName}>
              {carrier.carrierName}
            </option>
          ))}
        </Select>

        <TextInput
          labelText='Trailer Number'
          id='TrailerNumberInput'
          required
          value={trailerNumber}
          maxLength={25}
          onChange={handleTrailerNumberChange}
          data-testid='trailer-number-input'
        />
      </div>
    </TransactionDrawer>
  );
};
